<template>
  <section>
    <b-card no-body class="overflow-hidden" v-for="item in items">
      <b-row no-gutters>
        <b-col cols="6">
          <b-card-body>
            <b-card-text>
              <h5>
                {{ item.name }} {{ item.code }}
                <b-badge v-if="isAdvancedBook(item)" variant="info">{{ $t('dharma-application-form.application-list.badge.advanced-book') }}</b-badge>
                <b-badge v-if="isScrollPicture(item)" variant="info">{{ $t('dharma-application-form.application-list.badge.scroll-picture') }}</b-badge>
                <b-badge v-if="item.is_in_stock === false" variant="danger">{{ $t('dharma-application-form.application-list.badge.out-of-stock') }}</b-badge>
              </h5>
              <span v-if="item.author != '-'">{{ item.author }}</span>
            </b-card-text>
          </b-card-body>
        </b-col>
        <b-col cols="6">
          <b-list-group>
            <b-list-group-item>
              <p>{{ $t('dharma-application-form.application-list.label.quantity') }}</p>
              <input
                min="1"
                step="1"
                type="number"
                class="form-control"
                v-model="item.quantity"
              >
            </b-list-group-item>
            <b-list-group-item>
              <b-button block variant="outline-danger" @click="removeItem(item)">
                {{ $t('dharma-application-form.application-list.button.remove.label') }}
                <b-icon icon="trash" aria-hidden="true"></b-icon>
              </b-button>
            </b-list-group-item>
          </b-list-group>
        </b-col>
      </b-row>
    </b-card>
    <b-form id="form">
      <b-form-group 
        label-size="lg"
        :label="$t('dharma-application-form.application-list.label.purpose')"
        label-for="purpose"
        v-if="mustStatePurpose()"
      >
        <b-form-textarea id="purpose" v-model="purpose" trim></b-form-textarea>
        <template v-slot:description>
          <p class="text-primary font-weight-bold" v-html="$t('dharma-application-form.application-list.instruction-text.purpose')"></p>
        </template>
      </b-form-group>
      <b-form-group 
        label-size="lg"
        :label="$t('dharma-application-form.application-list.label.purpose_detail')"
        label-for="purpose_detail"
        v-if="mustProvidePurposeDetail()"
      >
        <b-form-textarea id="purpose_detail" v-model="purpose_detail" trim required></b-form-textarea>
        <template v-slot:description>
          <p class="text-primary font-weight-bold">{{ $t('dharma-application-form.application-list.instruction-text.purpose_detail') }}</p>
        </template>
      </b-form-group>
      <b-form-row>
        <b-col class="text-danger" v-if="!validation_results.list.succeeded">
          <p class="font-weight-bold" v-for="field in validation_results.list.fieldErrors">{{ field.message }}</p>
        </b-col>
      </b-form-row>
    </b-form>
  </section>
</template>

<script>
import Vue from 'vue'
import VueLocalStorage from 'vue-localstorage'

import { BadgePlugin, CardPlugin } from 'bootstrap-vue'
import { FormFilePlugin } from 'bootstrap-vue'
import { FormGroupPlugin } from 'bootstrap-vue'
import { FormPlugin } from 'bootstrap-vue'
import { FormTextareaPlugin } from 'bootstrap-vue'
import { IconsPlugin } from 'bootstrap-vue'
import { LayoutPlugin } from 'bootstrap-vue'
import { ListGroupPlugin } from 'bootstrap-vue'
import { Validators, createFormValidation } from '@lemoncode/fonk'
import { arrayRequired } from '@lemoncode/fonk-array-required-validator'
import { requiredByField } from '@lemoncode/fonk-required-by-field-validator'

import i18n from '@/plugins/i18n.js'

import BookApiService from '@/services/budaedu-api-services/book-api-service.js'
import PictureApiService from '@/services/budaedu-api-services/picture-api-service.js'
import ArrayElementsMatchConditionValidator from '@/services/fonk-validation-service/array-elements-match-condition-validator'

Vue.use(BadgePlugin)
Vue.use(CardPlugin)
Vue.use(FormFilePlugin)
Vue.use(FormGroupPlugin)
Vue.use(FormPlugin)
Vue.use(FormTextareaPlugin)
Vue.use(IconsPlugin)
Vue.use(LayoutPlugin)
Vue.use(ListGroupPlugin)
Vue.use(VueLocalStorage)

Vue.use(BookApiService)
Vue.use(PictureApiService)

export default {
  i18n,
  name: 'application-list',
  data() {
    return {
      items: [],
      purpose: '',
      purpose_detail: '',
      validation_results: {
        list: {},
        detail: {}
      }
    }
  },
  computed: {
    validation_schema() {
      return {
        field: {
          items: [
            {
              validator: arrayRequired.validator,
              message: this.$t('dharma-application-form.application-list.error-message.items.required')
            },
            {
              validator: ArrayElementsMatchConditionValidator,
              message: this.$t('dharma-application-form.application-list.error-message.items.quantity-not-less-than-one'),
              customArgs: {
                condition: (element) => element.quantity >= 1
              }
            },
            {
              validator: ArrayElementsMatchConditionValidator,
              message: this.$t('dharma-application-form.application-list.error-message.items.item-out-of-stock'),
              customArgs: {
                condition: (element) => element.is_in_stock
              }
            }
          ],
          purpose: [{
            validator: requiredByField.validator,
            customArgs: {
              field: 'items',
              condition: (field_value) => this.mustStatePurpose()
            },
            message: this.$t('dharma-application-form.application-list.error-message.purpose.required')
          }],
          purpose_detail: [{
            validator: requiredByField.validator,
            customArgs: {
              field: 'items',
              condition: (field_value) => this.mustProvidePurposeDetail()
            },
            message: this.$t('dharma-application-form.application-list.error-message.purpose_detail.required')
          }]
        }
      }
    }
  },
  watch: {
    items: {
      handler(new_value) {
        this.updateItemsStockStatus();
        this.$localStorage.set('application-items', JSON.stringify(new_value))
        this.$emit('update-items', new_value)
      },
      deep: true
    },
    purpose(new_value) {
      this.$localStorage.set('application-purpose', new_value)
      this.$emit('update-purpose', new_value)
    },
    purpose_detail(new_value) {
      this.$localStorage.set('application-purpose-detail', new_value)
      this.$emit('update-purpose-detail', new_value)
    }
  },
  methods: {
    removeItem(item) {
      const index = this.items.indexOf(item)
      this.items.splice(index, 1)
    },
    async validateForm() {
      const validation = createFormValidation(this.validation_schema)

      await validation
        .validateForm({ items: this.items, purpose: this.purpose, purpose_detail: this.purpose_detail })
        .then(form_validation_result => { this.validation_results.list = form_validation_result })

      return this.validation_results.list.succeeded
    },
    isPicture(item) {
      if (item.apply_type === 'picture') return true;
      if (item.apply_type === 'book') return false;
      // 回退機制 (Fallback)
      return item.code.startsWith('BM') || item.code.startsWith('BP') || item.code.startsWith('BD');
    },
    isBook(item) {
      if (item.apply_type === 'book') return true;
      if (item.apply_type === 'picture') return false;
      // 回退機制 (Fallback)
      return !this.isPicture(item);
    },
    isScrollPicture(item) {
      if (!this.isPicture(item)) return false;
      // 統一檢查 type, is_scroll 或 category 屬性
      return item.type === 'scroll' || item.is_scroll === 'Y' || item.is_scroll === true || item.category === 'scroll';
    },
    isAdvancedBook(item) {
      if (!this.isBook(item)) return false;
      // 統一檢查 advanced 或 category 屬性
      return item.advanced === 'Y' || item.advanced === true || item.category === 'advanced';
    },
    mustStatePurpose() {
      const initial_quantity = 0

      const book_quantity = this.items
        .filter(item => this.isBook(item))
        .reduce((accumulator, item) => accumulator + parseInt(item.quantity), initial_quantity)
      const advanced_book_quantity = this.items
        .filter(item => this.isAdvancedBook(item))
        .reduce((accumulator, item) => accumulator + parseInt(item.quantity), initial_quantity)
      const scroll_picture_quantity = this.items
        .filter(item => this.isScrollPicture(item))
        .reduce((accumulator, item) => accumulator + parseInt(item.quantity), initial_quantity)

      return book_quantity > 5 || advanced_book_quantity >= 1 || scroll_picture_quantity >= 1
    },
    mustProvidePurposeDetail() {
      const initial_quantity = 0

      const book_quantity = this.items
        .filter(item => this.isBook(item))
        .reduce((accumulator, item) => accumulator + parseInt(item.quantity), initial_quantity)

      return book_quantity > 50
    },
    async updateItemsStockStatus() {
      for (const item of this.items) {
        const is_in_stock = await this.checkItemStock(item);
        this.$set(item, 'is_in_stock', is_in_stock);
      }
    },
    async checkItemStock(item) {
      let result;
      if (this.isPicture(item)) {
        result = await this.$pictureApiService.getByCode(item.code);
      } else {
        result = await this.$bookApiService.getByCode(item.code);
      }
      
      return result && result.data && result.data.in_stock === 'Y';
    }
  },
  created() {
    // alert(`Vue version : ${Vue.version}`)
    this.items = JSON.parse(this.$localStorage.get('application-items', '[]'))
    this.purpose = this.$localStorage.get('application-purpose', '')
    this.purpose_detail = this.$localStorage.get('application-purpose-detail', '')
    this.updateItemsStockStatus();
  },
  components: {
    // 
  },
  install(Vue) {
    Vue.prototype.$dharmaApplicationList = {
      getItems() {
        const application_items = Vue.prototype.$localStorage.get('application-items')
        return application_items != null ? JSON.parse(application_items) : []
      },
      checkItemExists(item) {
        const items = this.getItems()
        return items.findIndex(element => element.code === item.code) !== -1
      },
      addItem(item) {
        if (!this.checkItemExists(item)) {
          let items = this.getItems()
          if (!item.quantity) {
            item.quantity = 1
          }
          items.push(item)
          Vue.prototype.$localStorage.set('application-items', JSON.stringify(items))
        }
      }
    }
  }
}
</script>